import { BundleUsage } from "@/pages/users/types/user";

export interface CompletedSessionProfessional {
  key: string;
  name: string;
  // Held sessions with this professional, and when they span: first/last are
  // session end_times, so the range covers the actual working relationship.
  sessionCount: number;
  firstSessionAt: string;
  lastSessionAt: string;
}

/**
 * Professionals who actually held a session on this usage. A session counts
 * only when it happened: `active` (never cancelled — there is no "completed"
 * status), already ended, and not a no-show or marked unsuccessful.
 * Session.professional_id is frozen at booking time, so session rows are the
 * history; usage.professional is only the *current* assignment and says
 * nothing about who held past sessions.
 */
export function getCompletedSessionProfessionals(
  usage: BundleUsage,
): CompletedSessionProfessional[] {
  const now = Date.now();
  const byKey = new Map<string, CompletedSessionProfessional>();

  for (const session of usage.sessions ?? []) {
    const professional = session.professional;
    if (
      session.status !== "active" ||
      new Date(session.end_time).getTime() >= now ||
      session.is_unsuccessful === true ||
      session.is_no_show === true ||
      !professional
    ) {
      continue;
    }

    const key = session.professional_id ?? professional.name;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        name: professional.name,
        sessionCount: 1,
        firstSessionAt: session.end_time,
        lastSessionAt: session.end_time,
      });
    } else {
      existing.sessionCount += 1;
      if (session.end_time < existing.firstSessionAt) {
        existing.firstSessionAt = session.end_time;
      }
      if (session.end_time > existing.lastSessionAt) {
        existing.lastSessionAt = session.end_time;
      }
    }
  }

  return [...byKey.values()].sort((a, b) =>
    b.lastSessionAt.localeCompare(a.lastSessionAt),
  );
}

export function getUsageProductName(usage: BundleUsage): string | null {
  return usage.bundle?.product?.name ?? null;
}
